import { Button, Card, SectionLabel } from '@wundo/ui';
import type { AudioSetup } from '@/lib/useAudioSetup.ts';
import { PracticeAudioSetup } from './PracticeAudioSetup.tsx';
import { m } from '@/paraglide/messages.js';

export interface PracticeVoiceSetupProps {
  /** The devices to pick from, live. */
  audio: AudioSetup;
  starting: boolean;
  failed: boolean;
  onStart: () => void;
  onBack: () => void;
  /** Give up on the microphone and have the same conversation in writing. */
  onChatInstead: () => void;
}

/**
 * The audio check, between choosing to speak and the conversation itself.
 *
 * The microphone is asked for here and nowhere earlier: a prompt before the
 * user has asked to speak gets a reflexive Block the browser then remembers.
 * A refused microphone stops here rather than starting a call that dies half
 * a second in.
 */
export function PracticeVoiceSetup({
  audio,
  starting,
  failed,
  onStart,
  onBack,
  onChatInstead,
}: PracticeVoiceSetupProps) {
  return (
    <Card as='section'>
      <SectionLabel as='h2'>{m.practice_session_audio_check()}</SectionLabel>

      <PracticeAudioSetup setup={audio} />

      {failed && (
        <p role='alert' className='mt-3 text-base text-danger'>
          {m.practice_session_begin_failed()}
        </p>
      )}

      <div className='mt-5 flex flex-wrap items-center gap-3'>
        <Button disabled={starting || audio.block !== null} onClick={onStart}>
          {starting ? m.practice_session_beginning() : m.practice_session_audio_start()}
        </Button>

        {audio.block !== null && (
          <Button variant='quiet' disabled={starting} onClick={onChatInstead}>
            {m.practice_session_audio_chat_instead()}
          </Button>
        )}

        <Button variant='ghost' disabled={starting} onClick={onBack}>
          {m.practice_session_audio_back()}
        </Button>
      </div>
    </Card>
  );
}
